import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../lib/axios';
import { Baby, Calendar, AlertTriangle, FileText, Clock, Activity, LogIn, LogOut, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const ChildProfile = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [classroom, setClassroom] = useState(null);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [healthChecks, setHealthChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showHealthCheckModal, setShowHealthCheckModal] = useState(false);
  const [showMealModal, setShowMealModal] = useState(false);
  const [showNapModal, setShowNapModal] = useState(false);
  const [showDiaperModal, setShowDiaperModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);

  // Today's log
  const [todayLog, setTodayLog] = useState(null);
  const today = new Date().toISOString().split('T')[0];

  // Signature pad ref
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Form states
  const [healthCheckForm, setHealthCheckForm] = useState({
    check_type: 'drop_off',
    guardian_name: '',
    check_time: new Date().toTimeString().slice(0, 5),
    no_visible_marks: true,
    parent_signature: '',
  });

  const [mealForm, setMealForm] = useState({
    meal_type: 'breakfast',
    time: new Date().toTimeString().slice(0, 5),
    food_items: '',
    amount_eaten: 'all',
  });

  const [napForm, setNapForm] = useState({
    start_time: new Date().toTimeString().slice(0, 5),
    end_time: '',
  });

  const [diaperForm, setDiaperForm] = useState({
    time: new Date().toTimeString().slice(0, 5),
    type: 'wet',
    rash_noted: false,
  });

  const [activityForm, setActivityForm] = useState({
    time: new Date().toTimeString().slice(0, 5),
    activity_name: '',
    developmental_areas: [],
    description: '',
    child_participation: 'active',
  });

  useEffect(() => {
    fetchChildData();
  }, [childId]);

  const fetchChildData = async () => {
    try {
      const [childRes, dailyLogsRes, incidentsRes, healthChecksRes] = await Promise.all([
        axios.get(`/children/${childId}`),
        axios.get(`/daily-logs?child_id=${childId}`),
        axios.get(`/incident-reports?child_id=${childId}`),
        axios.get(`/health-checks?child_id=${childId}`),
      ]);
      
      setChild(childRes.data);
      setDailyLogs(dailyLogsRes.data.slice(0, 10));
      setIncidents(incidentsRes.data.slice(0, 5));
      setHealthChecks(healthChecksRes.data.slice(0, 5));

      // Fetch classroom info
      if (childRes.data.classroom_id) {
        const classroomRes = await axios.get(`/classrooms/${childRes.data.classroom_id}`);
        setClassroom(classroomRes.data);
      }

      // Get today's log
      const todayLogRes = await axios.get(`/daily-logs-complete/${childId}/${today}`);
      if (todayLogRes.data) {
        setTodayLog(todayLogRes.data);
      }
    } catch (error) {
      console.error('Error fetching child data:', error);
      toast.error('Failed to load child profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    try {
      const currentTime = new Date().toTimeString().slice(0, 5);
      await axios.post('/daily-logs-complete', {
        child_id: childId,
        date: today,
        arrival_time: currentTime,
        dropped_by: 'Parent/Guardian',
      });
      toast.success(`${child.name} checked in at ${currentTime}`);
      fetchChildData();
    } catch (error) {
      console.error('Error checking in:', error);
      toast.error('Failed to check in');
    }
  };

  const handleCheckOut = async () => {
    try {
      const currentTime = new Date().toTimeString().slice(0, 5);
      await axios.patch(`/daily-logs-complete/${todayLog.id}`, {
        pickup_time: currentTime,
        picked_up_by: 'Parent/Guardian',
      });
      toast.success(`${child.name} checked out at ${currentTime}`);
      fetchChildData();
    } catch (error) {
      console.error('Error checking out:', error);
      toast.error('Failed to check out');
    }
  };

  const handleHealthCheckSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/health-checks-complete', {
        child_id: childId,
        check_date: today,
        ...healthCheckForm,
      });
      toast.success('Health check recorded successfully!');
      setShowHealthCheckModal(false);
      fetchChildData();
    } catch (error) {
      console.error('Error submitting health check:', error);
      toast.error('Failed to record health check');
    }
  };

  const handleMealSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/daily-logs/quick-meal', {
        child_id: childId,
        date: today,
        ...mealForm,
      });
      toast.success('Meal logged successfully!');
      setShowMealModal(false);
      fetchChildData();
    } catch (error) {
      console.error('Error logging meal:', error);
      toast.error('Failed to log meal');
    }
  };

  const handleNapSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/daily-logs/quick-nap', {
        child_id: childId,
        date: today,
        ...napForm,
      });
      toast.success('Nap logged successfully!');
      setShowNapModal(false);
      fetchChildData();
    } catch (error) {
      console.error('Error logging nap:', error);
      toast.error('Failed to log nap');
    }
  };

  const handleDiaperSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/daily-logs/quick-diaper', {
        child_id: childId,
        date: today,
        ...diaperForm,
      });
      toast.success('Diaper change logged successfully!');
      setShowDiaperModal(false);
      fetchChildData();
    } catch (error) {
      console.error('Error logging diaper:', error);
      toast.error('Failed to log diaper change');
    }
  };

  const handleActivitySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/daily-logs/quick-activity', {
        child_id: childId,
        date: today,
        ...activityForm,
      });
      toast.success('Activity logged successfully!');
      setShowActivityModal(false);
      fetchChildData();
    } catch (error) {
      console.error('Error logging activity:', error);
      toast.error('Failed to log activity');
    }
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let months = (today.getFullYear() - birthDate.getFullYear()) * 12;
    months -= birthDate.getMonth();
    months += today.getMonth();
    
    if (months < 12) {
      return `${months} months`;
    } else {
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      return remainingMonths > 0 ? `${years} year${years > 1 ? 's' : ''} ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}` : `${years} year${years > 1 ? 's' : ''}`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="text-center py-12">
        <Baby className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Child not found</h3>
      </div>
    );
  }

  const isCheckedIn = todayLog && todayLog.arrival_time && !todayLog.pickup_time;

  // Signature pad functions
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e293b';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    // Save signature as base64
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const signatureData = canvas.toDataURL('image/png');
      setHealthCheckForm(prev => ({ ...prev, parent_signature: signatureData }));
    }
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHealthCheckForm(prev => ({ ...prev, parent_signature: '' }));
    }
  };

  // Photo upload handler
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Convert to base64 for simple storage (in production, use proper file upload)
    const reader = new FileReader();
    reader.onload = async (event) => {
      const photoUrl = event.target?.result;
      try {
        await axios.patch(`/children/${childId}/photo`, null, {
          params: { photo_url: photoUrl }
        });
        setChild(prev => ({ ...prev, photo_url: photoUrl }));
        toast.success('Photo updated!');
      } catch (error) {
        console.error('Error uploading photo:', error);
        toast.error('Failed to upload photo');
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6" data-testid="child-profile-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate(-1)}
        className="mb-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            {/* Child Photo with Upload */}
            <div className="relative group">
              {child.photo_url ? (
                <img 
                  src={child.photo_url} 
                  alt={child.name}
                  className="w-20 h-20 rounded-full object-cover border-2 border-slate-200"
                />
              ) : (
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                  <Baby className="w-10 h-10 text-primary" />
                </div>
              )}
              <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                <span className="text-white text-xs font-medium">📷 Edit</span>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </label>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {child.name}
              </h1>
              <div className="flex items-center gap-4 mt-2">
                <span className="text-slate-600 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {calculateAge(child.dob)} • DOB: {new Date(child.dob).toLocaleDateString()}
                </span>
                {classroom && (
                  <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                    {classroom.name}
                  </span>
                )}
                {isCheckedIn && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>
                    Present
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Check-in/Check-out buttons */}
          <div className="flex gap-2">
            {!isCheckedIn ? (
              <Button 
                onClick={handleCheckIn}
                className="rounded-full"
                data-testid="check-in-button"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Check In
              </Button>
            ) : (
              <Button 
                onClick={handleCheckOut}
                variant="outline"
                className="rounded-full"
                data-testid="check-out-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Check Out
              </Button>
            )}
          </div>
        </div>

        {child.allergies && (
          <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              <div>
                <p className="font-semibold text-rose-900">Allergy Alert</p>
                <p className="text-sm text-rose-700">{child.allergies}</p>
              </div>
            </div>
          </div>
        )}

        {child.notes && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-slate-700"><span className="font-semibold">Notes:</span> {child.notes}</p>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-5 h-5 text-blue-600" />
            <p className="text-sm font-medium text-slate-600">Daily Logs</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">{dailyLogs.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <p className="text-sm font-medium text-slate-600">Incidents</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">{incidents.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-5 h-5 text-green-600" />
            <p className="text-sm font-medium text-slate-600">Health Checks</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">{healthChecks.length}</p>
        </div>
      </div>

      {/* Quick Actions - Only show if checked in */}
      {isCheckedIn && (
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button 
              className="h-20 flex-col gap-2" 
              variant="outline"
              data-testid="add-health-check-button"
              onClick={() => setShowHealthCheckModal(true)}
            >
              <FileText className="w-6 h-6" />
              <span className="text-sm">Add Health Check</span>
            </Button>
            <Button 
              className="h-20 flex-col gap-2" 
              variant="outline"
              onClick={() => setShowMealModal(true)}
            >
              <span className="text-2xl">🍴</span>
              <span className="text-sm">Log Meal</span>
            </Button>
            <Button 
              className="h-20 flex-col gap-2" 
              variant="outline"
              onClick={() => setShowNapModal(true)}
            >
              <span className="text-2xl">🌙</span>
              <span className="text-sm">Log Nap</span>
            </Button>
            <Button 
              className="h-20 flex-col gap-2" 
              variant="outline"
              onClick={() => setShowDiaperModal(true)}
            >
              <span className="text-2xl">💧</span>
              <span className="text-sm">Diaper/Bathroom</span>
            </Button>
            <Button 
              className="h-20 flex-col gap-2" 
              variant="outline"
              onClick={() => setShowActivityModal(true)}
            >
              <span className="text-2xl">⚡</span>
              <span className="text-sm">Log Activity</span>
            </Button>
            <Button 
              className="h-20 flex-col gap-2" 
              variant="outline"
              onClick={() => toast.info('Photo upload coming soon')}
            >
              <span className="text-2xl">📸</span>
              <span className="text-sm">Add Photo</span>
            </Button>
            <Button 
              className="h-20 flex-col gap-2" 
              variant="outline"
              onClick={() => toast.info('Incident report coming soon')}
            >
              <AlertTriangle className="w-6 h-6" />
              <span className="text-sm">Report Incident</span>
            </Button>
            <Button 
              className="h-20 flex-col gap-2 bg-primary text-white hover:bg-primary/90" 
              onClick={() => toast.success('Notification sent to parent!')}
              data-testid="share-with-parent-button"
            >
              <span className="text-2xl">📱</span>
              <span className="text-sm">Share with Parent</span>
            </Button>
          </div>
        </div>
      )}

      {!isCheckedIn && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <Clock className="w-12 h-12 text-amber-600 mx-auto mb-3" />
          <h3 className="font-semibold text-amber-900 mb-2">Child Not Checked In</h3>
          <p className="text-sm text-amber-700">Please check in {child.name} to start logging activities</p>
        </div>
      )}

      {/* Rest of existing tabs */}
      <Tabs defaultValue="timeline" className="space-y-4">
        <TabsList className="bg-white border border-slate-100">
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="daily-logs">Daily Logs</TabsTrigger>
          <TabsTrigger value="incidents">Incidents</TabsTrigger>
          <TabsTrigger value="health-checks">Health Checks</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Recent Activity
          </h2>
          {[...dailyLogs, ...incidents, ...healthChecks]
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 10)
            .map((item, idx) => (
              <div key={idx} className="bg-white rounded-lg border border-slate-100 p-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-slate-600">
                      {new Date(item.created_at).toLocaleString()}
                    </p>
                    <p className="text-slate-900 mt-1">
                      {item.description || item.notes_to_parent || 'Activity logged'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
        </TabsContent>

        <TabsContent value="daily-logs" className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Daily Logs
          </h2>
          {dailyLogs.map((log) => (
            <div key={log.id} className="bg-white rounded-lg border border-slate-100 p-4">
              <p className="font-semibold text-slate-900">{new Date(log.date).toLocaleDateString()}</p>
              {log.notes_to_parent && (
                <p className="text-sm text-slate-600 mt-2">{log.notes_to_parent}</p>
              )}
            </div>
          ))}
          {dailyLogs.length === 0 && (
            <p className="text-center text-slate-600 py-8">No daily logs yet</p>
          )}
        </TabsContent>

        <TabsContent value="incidents" className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Incidents
          </h2>
          {incidents.map((incident) => (
            <div key={incident.id} className="bg-white rounded-lg border border-slate-100 p-4">
              <div className="flex items-start justify-between mb-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  incident.incident_type === 'injury' ? 'bg-rose-100 text-rose-700' :
                  incident.incident_type === 'illness' ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-700'
                }`}>
                  {incident.incident_type}
                </span>
                <span className="text-sm text-slate-600">
                  {new Date(incident.datetime).toLocaleDateString()}
                </span>
              </div>
              <p className="text-slate-900">{incident.description}</p>
            </div>
          ))}
          {incidents.length === 0 && (
            <p className="text-center text-slate-600 py-8">No incidents reported</p>
          )}
        </TabsContent>

        <TabsContent value="health-checks" className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Health Checks
          </h2>
          {healthChecks.map((check) => (
            <div key={check.id} className="bg-white rounded-lg border border-slate-100 p-4">
              <div className="flex items-start justify-between mb-2">
                <span className="text-sm font-medium text-slate-900">
                  {check.observation_time}
                </span>
                <span className="text-sm text-slate-600">
                  {new Date(check.datetime).toLocaleDateString()}
                </span>
              </div>
              {check.symptoms && check.symptoms.length > 0 && (
                <p className="text-sm text-slate-600">Symptoms: {check.symptoms.join(', ')}</p>
              )}
              {check.notes && (
                <p className="text-sm text-slate-600 mt-2">{check.notes}</p>
              )}
            </div>
          ))}
          {healthChecks.length === 0 && (
            <p className="text-center text-slate-600 py-8">No health checks recorded</p>
          )}
        </TabsContent>
      </Tabs>

      {/* Health Check Modal */}
      <Dialog open={showHealthCheckModal} onOpenChange={setShowHealthCheckModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Health Check - {healthCheckForm.check_type === 'drop_off' ? 'Drop-off' : 'Pick-up'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleHealthCheckSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Check Type</Label>
                <Select
                  value={healthCheckForm.check_type}
                  onValueChange={(value) => setHealthCheckForm({...healthCheckForm, check_type: value})}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="drop_off">Drop-off (AM)</SelectItem>
                    <SelectItem value="pick_up">Pick-up (PM)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={healthCheckForm.check_time}
                  onChange={(e) => setHealthCheckForm({...healthCheckForm, check_time: e.target.value})}
                  required
                  className="mt-1.5"
                />
              </div>
            </div>
            <div>
              <Label>Parent/Guardian Name</Label>
              <Input
                value={healthCheckForm.guardian_name}
                onChange={(e) => setHealthCheckForm({...healthCheckForm, guardian_name: e.target.value})}
                placeholder="Full name of person dropping off/picking up"
                required
                className="mt-1.5"
              />
            </div>
            
            {/* Health Check Confirmation */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-sm text-slate-700 mb-3">
                <strong>Texas Licensing Requirement:</strong> I confirm that I have visually inspected the child and:
              </p>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={healthCheckForm.no_visible_marks}
                  onCheckedChange={(checked) => setHealthCheckForm({...healthCheckForm, no_visible_marks: checked})}
                />
                <Label className="text-sm">No visible marks, bruises, cuts, or injuries observed</Label>
              </div>
              {!healthCheckForm.no_visible_marks && (
                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ If marks are observed, please document them in an incident report after completing check-in.
                </p>
              )}
            </div>

            {/* Parent Signature Pad */}
            <div>
              <Label className="mb-2 block">Parent/Guardian Signature <span className="text-red-500">*</span></Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white">
                <canvas
                  ref={signatureCanvasRef}
                  width={400}
                  height={120}
                  className="w-full cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-slate-500">Sign above using mouse or finger</p>
                <Button type="button" variant="ghost" size="sm" onClick={clearSignature}>
                  Clear Signature
                </Button>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button 
                type="submit" 
                className="flex-1"
                disabled={!healthCheckForm.guardian_name || !healthCheckForm.parent_signature}
              >
                Record Health Check
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowHealthCheckModal(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Meal Modal */}
      <Dialog open={showMealModal} onOpenChange={setShowMealModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Log Meal (USDA Tracking)</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMealSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Meal Type</Label>
              <Select
                value={mealForm.meal_type}
                onValueChange={(value) => setMealForm({...mealForm, meal_type: value})}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakfast">☀️ Breakfast</SelectItem>
                  <SelectItem value="am_snack">🍪 AM Snack</SelectItem>
                  <SelectItem value="lunch">🍽️ Lunch</SelectItem>
                  <SelectItem value="pm_snack">🍎 PM Snack</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Time</Label>
              <Input
                type="time"
                value={mealForm.time}
                onChange={(e) => setMealForm({...mealForm, time: e.target.value})}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Food Items</Label>
              <Input
                value={mealForm.food_items}
                onChange={(e) => setMealForm({...mealForm, food_items: e.target.value})}
                placeholder="e.g., Mac & cheese, apple slices"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Amount Eaten</Label>
              <Select
                value={mealForm.amount_eaten}
                onValueChange={(value) => setMealForm({...mealForm, amount_eaten: value})}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="most">Most</SelectItem>
                  <SelectItem value="some">Some</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="refused">Refused</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1">Log Meal</Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowMealModal(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Nap Modal */}
      <Dialog open={showNapModal} onOpenChange={setShowNapModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Log Nap</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleNapSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Start Time</Label>
              <Input
                type="time"
                value={napForm.start_time}
                onChange={(e) => setNapForm({...napForm, start_time: e.target.value})}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>End Time (optional)</Label>
              <Input
                type="time"
                value={napForm.end_time}
                onChange={(e) => setNapForm({...napForm, end_time: e.target.value})}
                className="mt-1.5"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1">Log Nap</Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowNapModal(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Diaper Modal */}
      <Dialog open={showDiaperModal} onOpenChange={setShowDiaperModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Log Diaper/Bathroom</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDiaperSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Time</Label>
              <Input
                type="time"
                value={diaperForm.time}
                onChange={(e) => setDiaperForm({...diaperForm, time: e.target.value})}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select
                value={diaperForm.type}
                onValueChange={(value) => setDiaperForm({...diaperForm, type: value})}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wet">Wet</SelectItem>
                  <SelectItem value="soiled">Soiled</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                  <SelectItem value="dry">Dry</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={diaperForm.rash_noted}
                onCheckedChange={(checked) => setDiaperForm({...diaperForm, rash_noted: checked})}
              />
              <Label>Rash noted</Label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1">Log Change</Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowDiaperModal(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Activity Modal */}
      <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Log Activity (Texas 4 Areas)</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleActivitySubmit} className="space-y-4 mt-4">
            <div>
              <Label>Activity Name</Label>
              <Input
                value={activityForm.activity_name}
                onChange={(e) => setActivityForm({...activityForm, activity_name: e.target.value})}
                placeholder="e.g., Block building, Story time"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Time</Label>
              <Input
                type="time"
                value={activityForm.time}
                onChange={(e) => setActivityForm({...activityForm, time: e.target.value})}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Developmental Areas (Texas)</Label>
              <div className="space-y-2 mt-2">
                {[
                  {value: 'physical_motor', label: 'Physical/Motor Skills'},
                  {value: 'social_emotional', label: 'Social-Emotional'},
                  {value: 'cognitive', label: 'Cognitive'},
                  {value: 'language_communication', label: 'Language/Communication'},
                ].map(area => (
                  <div key={area.value} className="flex items-center gap-2">
                    <Checkbox
                      checked={activityForm.developmental_areas.includes(area.value)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setActivityForm({...activityForm, developmental_areas: [...activityForm.developmental_areas, area.value]});
                        } else {
                          setActivityForm({...activityForm, developmental_areas: activityForm.developmental_areas.filter(a => a !== area.value)});
                        }
                      }}
                    />
                    <Label>{area.label}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={activityForm.description}
                onChange={(e) => setActivityForm({...activityForm, description: e.target.value})}
                placeholder="What did the child do?"
                required
                rows={3}
                className="mt-1.5"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1">Log Activity</Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowActivityModal(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChildProfile;
